import {
  effect,
  OutputEmitterRef,
  Signal,
  WritableSignal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { identity, Observable } from 'rxjs';

/**
 * Extract the content type of a signal.
 *
 * Example:
 * ```ts
 * const mySignal = signal({a: 'test', b: 2});
 * type MySignalContent = SignalContent<typeof mySignal>;
 * // MySignalContent will be { a: string, b: number }
 * ```
 */
export type SignalContent<T extends Signal<any>> = T extends Signal<infer P>
  ? P
  : never;

/**
 * Use this to reduce boilerplate when adding types to form group and form controls.
 *
 * Example:
 * ```ts
 * type MyType = { name: string };
 * const form = new FormGroup<FormGroupControlsOf<MyType>>({
 *   name: new FormControl('', [Validators.required]),
 * });
 * ```
 */
export type FormGroupControlsOf<T extends Record<string, any>> = {
  [K in keyof T]: FormControl<T[K] | null>;
};

/**
 * Bind a signal to an observable.
 * This will cause values emitted by the observable to be written
 * into the provided signal.
 */
export function bindSignalToObservable<T>(
  signal: WritableSignal<T>,
  observable: Observable<T>
): void;
/**
 * Bind a signal to an observable.
 * This will cause values emitted by the observable to be written
 * into the provided signal.
 */
export function bindSignalToObservable<T, U>(
  signal: WritableSignal<T>,
  observable: Observable<U>,
  mapFn: (v: U) => T
): void;
/**
 * Bind a signal to an observable.
 * This will cause values emitted by the observable to be written
 * into the provided signal.
 */
export function bindSignalToObservable<T>(
  signal: WritableSignal<T>,
  observable: Observable<any>,
  mapFn: (v: any) => T = identity
): void {
  effect((onCleanup) => {
    const subscription = observable.subscribe({
      next: (v) => signal.set(mapFn(v)),
    });
    onCleanup(() => {
      subscription.unsubscribe();
    });
  });
}

export type OutputSignalContent<T extends OutputEmitterRef<any>> =
  T extends OutputEmitterRef<infer P> ? P : never;

export function listenForEventsWhen<
  EventName extends keyof HTMLElementEventMap
>(
  predicate: () => boolean,
  target: HTMLElement,
  eventName: EventName,
  listener: (e: HTMLElementEventMap[EventName]) => void
): void;
export function listenForEventsWhen<EventName extends keyof WindowEventMap>(
  predicate: () => boolean,
  target: Window,
  eventName: EventName,
  listener: (e: WindowEventMap[EventName]) => void
): void;
export function listenForEventsWhen<EventName extends keyof DocumentEventMap>(
  predicate: () => boolean,
  target: Document,
  eventName: EventName,
  listener: (e: DocumentEventMap[EventName]) => void
): void;
export function listenForEventsWhen<
  EventName extends
    | keyof HTMLElementEventMap
    | keyof WindowEventMap
    | keyof DocumentEventMap
>(
  predicate: () => boolean,
  target: HTMLElement | Window | Document,
  eventName: EventName,
  listener: (e: any) => void
): void {
  effect((onCleanup) => {
    if (predicate()) {
      target.addEventListener(eventName, listener);
      onCleanup(() => {
        target.removeEventListener(eventName, listener);
      });
    }
  });
}
